/**
 * The composer's removable chips (docs/08 §2): an image with a tiny preview
 * and its upload state, and a website. A URL chip also renders the palette
 * the orchestrator pulled from the site, once the job has run and the job
 * detail carries it — that is how the user learns the brand was understood.
 */
import type { ReactNode } from "react";

function RemoveButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title="Remove"
      onClick={onClick}
      className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-pill text-slate-500 hover:bg-slate-200 hover:text-slate-900"
    >
      ×
    </button>
  );
}

export function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** A brand palette as a row of swatches; each colour is its own tooltip. */
export function Palette({ colours }: { colours: string[] }) {
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`Palette: ${colours.join(", ")}`}
      data-testid="palette"
    >
      {colours.map((c, i) => (
        <i
          key={`${c}-${i}`}
          title={c}
          className="inline-block h-3 w-3 rounded-sm border border-black/10"
          style={{ background: c }}
        />
      ))}
    </span>
  );
}

export interface UrlChipProps {
  url: string;
  /** From `job.pages[i].palette` once extracted; absent before the job runs. */
  palette?: string[] | null;
  onRemove?: () => void;
}

export function UrlChip({ url, palette, onRemove }: UrlChipProps) {
  return (
    <li
      className="tb-pill tb-pill-sm inline-flex items-center gap-1.5"
      data-testid="url-chip"
      title={url}
    >
      <span className="max-w-[16rem] truncate">{hostnameOf(url)}</span>
      {palette && palette.length > 0 ? <Palette colours={palette} /> : null}
      {onRemove ? (
        <RemoveButton label={`Remove ${url}`} onClick={onRemove} />
      ) : null}
    </li>
  );
}

export type ImageStatus = "uploading" | "ready" | "error";

export interface ImageChipProps {
  name: string;
  previewUrl: string | null;
  status: ImageStatus;
  /** The sentence for a failed upload; rendered as an alert beside the chip. */
  error?: ReactNode;
  /** Shown before the remove button (the composer's "Logo" switch). */
  extra?: ReactNode;
  onRemove: () => void;
}

export function ImageChip({
  name,
  previewUrl,
  status,
  error,
  extra,
  onRemove,
}: ImageChipProps) {
  return (
    <li
      className={[
        "tb-pill tb-pill-sm inline-flex items-center gap-1.5",
        status === "error" ? "tb-pill-danger" : "",
        status === "uploading" ? "opacity-70" : "",
      ].join(" ")}
      data-testid="image-chip"
      data-status={status}
      aria-busy={status === "uploading"}
      title={name}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          className="h-5 w-5 rounded-sm object-cover"
        />
      ) : null}
      <span className="max-w-[12rem] truncate">{name}</span>
      {status === "uploading" ? (
        <span className="text-xs text-slate-500">uploading…</span>
      ) : null}
      {status === "error" && error ? (
        <span role="alert" className="text-xs">
          {error}
        </span>
      ) : null}
      {extra}
      <RemoveButton label={`Remove ${name}`} onClick={onRemove} />
    </li>
  );
}
